<template>
  <div class="notes-container">
    <!-- 全屏加载覆盖 -->
    <div v-if="pageLoading" class="page-loading-overlay">
      <div class="loader-card">
        <div class="spinner"></div>
        <div class="loader-text">正在加载...</div>
      </div>
    </div>
    <!-- Top toolbar -->
    <div class="toolbar-apple notes-toolbar">
      <div class="toolbar-left">
        <h1 class="page-title">📝 记事本</h1>
        <div class="note-count-badge">{{ filteredNotes.length }} 条笔记</div>
      </div>
      <div class="toolbar-right">
        <div class="search-box">
          <span class="search-icon">🔎</span>
          <input v-model="keyword" placeholder="搜索标题、内容或标签..." />
        </div>
        <button class="btn-apple-primary" @click="newNote" :disabled="loading">
          <span class="btn-icon">➕</span>
          <span class="btn-text">新建</span>
        </button>
        <button class="btn-apple" @click="toggleTheme">
          <span class="btn-icon">{{ themeIcon }}</span>
          <span class="btn-text">主题</span>
        </button>
      </div>
    </div>

    <div class="notes-layout" :class="theme">
      <!-- Left: list -->
      <div class="notes-list">
        <div class="chips">
          <button class="chip" :class="{active: tagFilter===''}" @click="setTagFilter('')">全部</button>
          <button v-for="t in allTags" :key="t" class="chip" :class="{active: tagFilter===t}" @click="setTagFilter(t)">#{{ t }}</button>
        </div>

        <div class="note-item" v-for="note in filteredNotes" :key="note.id" :class="{active: note.id === activeId}" @click="openNote(note.id)">
          <div class="note-item-header">
            <div class="title">{{ note.title || '未命名笔记' }}</div>
            <button class="pin" @click.stop="togglePin(note)">{{ note.pinned ? '📌' : '📍' }}</button>
          </div>
          <div class="preview">{{ (note.content || '').replace(/\n/g, ' ').slice(0, 60) }}</div>
          <div class="meta">
            <span>{{ formatTime(note.updatedAt) }}</span>
            <div class="tags">
              <span class="tag" v-for="t in note.tags" :key="t">#{{ t }}</span>
            </div>
          </div>
        </div>

        <div v-if="loading" style="padding:8px;color:#9ca3af;">加载中...</div>
      </div>

      <!-- Right: editor -->
      <div class="notes-editor" v-if="current">
        <div class="editor-header">
          <input class="title-input" v-model="current.title" placeholder="输入标题..." @input="debouncedSave" />
          <div class="editor-actions">
            <input class="tag-input" v-model="tagInput" placeholder="添加标签后回车" @keyup.enter="addTag" />
            <button class="btn-apple" @click="togglePreview">{{ showPreview ? '编辑' : '预览' }}</button>
            <button class="btn-apple-success" @click="shareToClipboard">复制</button>
            <button class="btn-apple" @click="deleteNoteById(current.id)" :disabled="loading">删除</button>
          </div>
        </div>

        <div class="editor-body" v-if="!showPreview">
          <textarea v-model="current.content" class="content-area" placeholder="在这里记录你的灵感、会议纪要、待办拆解... 支持 Markdown 基础语法" @input="debouncedSave"></textarea>
        </div>
        <div class="preview-body" v-else>
          <div class="preview-card" v-html="renderedMarkdown"></div>
        </div>

        <div class="footer-meta">
          <span>自动保存 · {{ saveHint }}</span>
        </div>
      </div>

      <div v-else class="empty-state">
        <div class="empty-icon">🗒️</div>
        <div class="empty-title">开始你的第一条笔记</div>
        <div class="empty-desc">点击右上角「新建」即可创建。支持标签、置顶、搜索与 Markdown 预览。</div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { listNotes, createNote, updateNote, deleteNote, pinNote } from '../api/note'

// 简单 Markdown 渲染（只处理少数语法，避免引入额外依赖）
function simpleMarkdown(md) {
  if (!md) return ''
  return md
    .replace(/^###\s(.+)$/gm, '<h3>$1</h3>')
    .replace(/^##\s(.+)$/gm, '<h2>$1</h2>')
    .replace(/^#\s(.+)$/gm, '<h1>$1</h1>')
    .replace(/^>\s(.+)$/gm, '<blockquote>$1</blockquote>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\n/g, '<br/>')
}

export default {
  name: 'Notepad',
  setup() {
    const theme = ref(localStorage.getItem('notes_theme') || 'light')
    const keyword = ref('')
    const tagFilter = ref('')
    const tagInput = ref('')
    const showPreview = ref(false)
    const saveHint = ref('已保存')
    const loading = ref(false)
    const pageLoading = ref(true)

    const notes = ref([])
    const activeId = ref(null)

    // 统一把后端Note转换为前端展示结构
    const normalize = (n) => ({
      id: n.id,
      title: n.title || '',
      content: n.content || '',
      tags: (n.tags ? n.tags.split(',').filter(Boolean) : []),
      pinned: n.pinned === 1 || n.pinned === true,
      createdAt: n.createdTime ? new Date(n.createdTime).getTime() : Date.now(),
      updatedAt: n.updatedTime ? new Date(n.updatedTime).getTime() : Date.now()
    })

    const denormalize = (n) => ({
      title: n.title || '',
      content: n.content || '',
      tags: (n.tags || []).join(','),
      pinned: n.pinned ? 1 : 0
    })

    const loadFromServer = async () => {
      loading.value = true
      pageLoading.value = true
      try {
        const { data } = await listNotes({ keyword: keyword.value, tag: tagFilter.value })
        if (data && data.code === 200) {
          notes.value = (data.data || []).map(normalize)
          // 保持当前选择
          if (!notes.value.find(n => n.id === activeId.value)) {
            activeId.value = notes.value[0]?.id || null
          }
        }
      } finally {
        loading.value = false
        pageLoading.value = false
      }
    }

    let saveTimer = null
    const debouncedSave = () => {
      if (!current.value) return
      saveHint.value = '保存中...'
      if (saveTimer) clearTimeout(saveTimer)
      saveTimer = setTimeout(async () => {
        try {
          const payload = denormalize(current.value)
          await updateNote(current.value.id, payload)
          // 本地更新时间戳
          current.value.updatedAt = Date.now()
          saveHint.value = '已保存'
        } catch (e) {
          saveHint.value = '保存失败'
        }
      }, 400)
    }

    const newNote = async () => {
      loading.value = true
      try {
        const { data } = await createNote({ title: '', content: '', tags: '', pinned: 0 })
        if (data && data.code === 200) {
          const nn = normalize(data.data)
          notes.value.unshift(nn)
          activeId.value = nn.id
          saveHint.value = '已保存'
        }
      } finally {
        loading.value = false
      }
    }

    const openNote = id => {
      activeId.value = id
    }

    const deleteNoteById = async (id) => {
      loading.value = true
      try {
        await deleteNote(id)
        const idx = notes.value.findIndex(n => n.id === id)
        if (idx !== -1) notes.value.splice(idx, 1)
        if (activeId.value === id) activeId.value = notes.value[0]?.id || null
      } finally {
        loading.value = false
      }
    }

    const togglePin = async (note) => {
      const newVal = note.pinned ? 0 : 1
      try {
        await pinNote(note.id, newVal)
        note.pinned = !note.pinned
        note.updatedAt = Date.now()
      } catch (e) {
        // ignore
      }
    }

    const addTag = () => {
      const t = tagInput.value.trim()
      if (!t || !current.value) return
      if (!current.value.tags.includes(t)) {
        current.value.tags.push(t)
        debouncedSave()
      }
      tagInput.value = ''
    }

    const setTagFilter = (t) => {
      tagFilter.value = t
    }

    const toggleTheme = () => {
      theme.value = theme.value === 'light' ? 'dark' : theme.value === 'dark' ? 'sepia' : 'light'
      localStorage.setItem('notes_theme', theme.value)
    }

    const togglePreview = () => {
      showPreview.value = !showPreview.value
    }

    const shareToClipboard = async () => {
      try {
        const text = `# ${current.value.title}\n\n${current.value.content}`
        await navigator.clipboard.writeText(text)
        saveHint.value = '已复制到剪贴板'
      } catch (e) {
        saveHint.value = '复制失败'
      }
    }

    const renderedMarkdown = computed(() => simpleMarkdown(current.value?.content || ''))

    const formatTime = ts => {
      const d = new Date(ts)
      const pad = n => (n < 10 ? '0' + n : '' + n)
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
    }

    const current = computed(() => notes.value.find(n => n.id === activeId.value))

    const allTags = computed(() => {
      const set = new Set()
      notes.value.forEach(n => (n.tags || []).forEach(t => set.add(t)))
      return Array.from(set).sort()
    })

    const filteredNotes = computed(() => {
      // 服务端已根据 keyword/tag 过滤，这里只负责排序
      const list = notes.value.slice()
      list.sort((a, b) => {
        if (a.pinned !== b.pinned) return (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0)
        return (b.updatedAt || 0) - (a.updatedAt || 0)
      })
      return list
    })

    let listTimer = null
    const debouncedLoad = () => {
      if (listTimer) clearTimeout(listTimer)
      listTimer = setTimeout(loadFromServer, 300)
    }

    onMounted(async () => {
      await loadFromServer()
      // 若无笔记则自动新建一条
      if (!notes.value.length) {
        await newNote()
      }
    })

    watch([keyword, tagFilter], () => {
      debouncedLoad()
    })

    const themeIcon = computed(() => (theme.value === 'light' ? '🌞' : theme.value === 'dark' ? '🌙' : '📜'))

    return {
      theme,
      keyword,
      tagFilter,
      tagInput,
      showPreview,
      saveHint,
      loading,
      notes,
      filteredNotes,
      current,
      activeId,
      renderedMarkdown,
      themeIcon,
      // methods
      newNote,
      openNote,
      deleteNoteById,
      togglePin,
      addTag,
      setTagFilter,
      toggleTheme,
      togglePreview,
      debouncedSave,
      formatTime,
      pageLoading,
    }
  }
}
</script>

<style scoped>
/* 全屏加载覆盖样式（苹果风格） */
.page-loading-overlay {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(242, 242, 247, 0.8);
  backdrop-filter: saturate(180%) blur(12px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
}
.loader-card {
  background: white;
  border: 1px solid #e5e5ea;
  border-radius: 16px;
  padding: 16px 20px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.08);
  display: flex;
  align-items: center;
  gap: 12px;
}
.spinner {
  width: 20px;
  height: 20px;
  border: 3px solid #e5e5ea;
  border-top-color: #007AFF;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}
.loader-text { font-weight: 700; color: #1d1d1f; }
@keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }

.notes-container { position: relative; padding: 16px; }
.notes-toolbar { margin-bottom: 12px; }
.note-count-badge { background: rgba(0,122,255,.08); color: #007AFF; padding: 6px 10px; border-radius: 12px; font-weight: 700; }

.notes-layout { display: grid; grid-template-columns: 320px 1fr; gap: 12px; height: calc(100vh - 140px); }

.notes-list { background: rgba(255,255,255,.7); backdrop-filter: saturate(180%) blur(16px); border: 1px solid #e5e5ea; border-radius: 16px; padding: 12px; overflow: auto; }
.notes-editor { background: rgba(255,255,255,.7); backdrop-filter: saturate(180%) blur(16px); border: 1px solid #e5e5ea; border-radius: 16px; padding: 12px; display: flex; flex-direction: column; overflow: hidden; }

.chips { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 8px; }
.chip { padding: 6px 10px; border-radius: 999px; border: 1px solid #d1d1d6; background: white; cursor: pointer; font-size: 12px; }
.chip.active { background: #007AFF; color: white; border-color: transparent; }

.note-item { padding: 10px; border-radius: 12px; border: 1px solid transparent; cursor: pointer; transition: .2s; }
.note-item + .note-item { margin-top: 6px; }
.note-item:hover { background: rgba(0,0,0,.03); }
.note-item.active { background: linear-gradient(135deg, #007AFF1a 0%, #5856D61a 100%); border-color: #c7d7fe; }
.note-item-header { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.note-item-header .title { font-weight: 700; color: #1d1d1f; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
.note-item .preview { color: #6b7280; font-size: 12px; margin-top: 4px; }
.note-item .meta { display: flex; align-items: center; justify-content: space-between; margin-top: 6px; font-size: 12px; color: #9ca3af; }
.note-item .pin { background: transparent; border: none; cursor: pointer; font-size: 16px; }
.tag { background: #f3f4f6; color: #111827; border-radius: 999px; padding: 2px 8px; margin-left: 6px; font-size: 12px; }

.editor-header { display: flex; gap: 8px; align-items: center; justify-content: space-between; }
.title-input { flex: 1; font-size: 18px; font-weight: 700; border: none; outline: none; background: transparent; padding: 8px; border-radius: 8px; }
.editor-actions { display: flex; gap: 8px; }
.tag-input { width: 160px; padding: 8px; border-radius: 8px; border: 1px solid #d1d1d6; }

.editor-body { flex: 1; overflow: hidden; }
.content-area { width: 100%; height: 100%; border: none; outline: none; resize: none; padding: 12px; font-size: 14px; background: transparent; }
.preview-body { flex: 1; overflow: auto; }
.preview-card { padding: 12px; line-height: 1.7; }
.preview-card h1, .preview-card h2, .preview-card h3 { margin: 12px 0 8px; }
.preview-card blockquote { margin: 8px 0; padding: 8px 12px; background: #f9fafb; border-left: 3px solid #007AFF; color: #374151; border-radius: 6px; }
.preview-card code { background: #f3f4f6; padding: 2px 6px; border-radius: 6px; }

.footer-meta { padding-top: 8px; font-size: 12px; color: #6b7280; }

/* Themes */
.notes-layout.dark .notes-list, .notes-layout.dark .notes-editor { background: rgba(28,28,30,.7); border-color: #3a3a3c; }
.notes-layout.dark .title-input, .notes-layout.dark .content-area, .notes-layout.dark .preview-card { color: #fff; }
.notes-layout.dark .note-item-header .title { color: #fff; }
.notes-layout.dark .note-item .preview, .notes-layout.dark .note-item .meta { color: #d1d1d6; }
.notes-layout.dark .chip { background: #1c1c1e; color: #fff; border-color: #3a3a3c; }

.notes-layout.sepia .notes-list, .notes-layout.sepia .notes-editor { background: #f7f0e1; border-color: #e5dac5; }
.notes-layout.sepia .content-area, .notes-layout.sepia .preview-card { color: #4b4234; }

/* Mobile */
@media (max-width: 900px) {
  .notes-layout { grid-template-columns: 1fr; height: auto; }
  .notes-editor { min-height: 60vh; }
}

/* Reuse buttons from App.vue apple style */
.btn-apple { padding: 8px 12px; border-radius: 10px; border: 1.5px solid #d1d1d6; background: white; cursor: pointer; font-weight: 600; }
.btn-apple-primary { padding: 8px 12px; border-radius: 10px; background: linear-gradient(135deg, #007AFF 0%, #5856D6 100%); color: white; border: none; font-weight: 700; cursor: pointer; }
.btn-apple-success { padding: 8px 12px; border-radius: 10px; background: linear-gradient(135deg, #34C759 0%, #30D158 100%); color: white; border: none; font-weight: 700; cursor: pointer; }

.search-box { display: flex; align-items: center; gap: 6px; padding: 6px 10px; border: 1px solid #d1d1d6; border-radius: 10px; background: white; }
.search-box input { border: none; outline: none; background: transparent; min-width: 220px; }

/* 移动端优化：编辑区按钮不溢出屏幕 */
@media (max-width: 768px) {
  .notes-editor { overflow: visible; }
  .editor-header { flex-direction: column; align-items: stretch; gap: 8px; }
  .editor-actions { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; width: 100%; }
  .editor-actions .btn-apple,
  .editor-actions .btn-apple-success { width: 100%; justify-content: center; }
  .tag-input { width: 100%; }
  .title-input { padding: 10px; font-size: 16px; }
  .content-area { min-height: 40vh; }
}

/* 超小屏优化：单列布局，避免按钮溢出 */
@media (max-width: 480px) {
  .editor-header { gap: 6px; }
  .editor-actions { grid-template-columns: 1fr; }
  .editor-actions .btn-apple,
  .editor-actions .btn-apple-success { padding: 10px; font-size: 14px; }
  .title-input { width: 100%; }
  .notes-editor { padding: 10px; }
}

/* 移动端工具栏适配与输入宽度修复 */
@media (max-width: 768px) {
  .notes-toolbar { display: flex; flex-wrap: wrap; gap: 8px; }
  .notes-toolbar .toolbar-left { flex: 1 1 100%; }
  .notes-toolbar .toolbar-right { flex: 1 1 100%; display: flex; flex-wrap: wrap; gap: 8px; }
  .notes-toolbar .search-box { flex: 1 1 100%; }
  .notes-toolbar .search-box input { min-width: 0; width: 100%; }
  .content-area { padding-bottom: 80px; }
}
</style>
